import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../../supabaseClient';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  Chip
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const formatSafeLocalDate = (dateStr, locales = undefined) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-');
    return new Date(year, month - 1, day).toLocaleDateString(locales);
  }
  return new Date(dateStr).toLocaleDateString(locales);
};

const LogBookReports = ({ user }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [rcdData, setRcdData] = useState([]);
  const [accountingData, setAccountingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [voucherType, setVoucherType] = useState('all');
  const [voucherTypes, setVoucherTypes] = useState(['all']);

  const printRef = useRef();

  useEffect(() => {
    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    loadVoucherTypes();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadReports();
    }
  }, [startDate, endDate, activeTab, voucherType]);

  const loadReports = async () => {
    setLoading(true);
    setError('');

    try {
      if (activeTab === 0) {
        // RCD Tab - Load check data
        await loadRCDData();
      } else {
        // Accounting Tab - Load accounting data
        await loadAccountingData();
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const loadVoucherTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('elogbook_transactions')
        .select('voucher_type', { distinct: true });

      if (error) throw error;

      const types = Array.from(new Set([...(data || []).map((item) => item.voucher_type).filter(Boolean)]));
      setVoucherTypes(['all', ...types]);
    } catch (err) {
      console.error('Error loading voucher types:', err);
    }
  };

  const loadRCDData = async () => {
    try {
      let query = supabase
        .from('elogbook_transactions')
        .select('*')
        .not('check_date', 'is', null)
        .gte('check_date', startDate)
        .lte('check_date', endDate);

      if (voucherType && voucherType !== 'all') {
        query = query.eq('voucher_type', voucherType);
      }

      const { data, error } = await query.order('check_number', { ascending: true });

      if (error) throw error;

      setRcdData(data || []);
    } catch (err) {
      console.error('Error loading RCD data:', err);
      throw err;
    }
  };

  const loadAccountingData = async () => {
    // For now, load similar data. This can be customized based on accounting requirements
    try {
      let query = supabase
        .from('elogbook_transactions')
        .select('*')
        .gte('created_at', `${startDate}T00:00:00.000Z`)
        .lte('created_at', `${endDate}T23:59:59.999Z`);

      if (voucherType && voucherType !== 'all') {
        query = query.eq('voucher_type', voucherType);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      setAccountingData(data || []);
    } catch (err) {
      console.error('Error loading accounting data:', err);
      throw err;
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: activeTab === 0 ? 'RCD Report' : 'Accounting Report',
  });

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(activeTab === 0 ? 'RCD Report' : 'Accounting Report');

      // Add title
      worksheet.addRow([activeTab === 0 ? 'RCD Report' : 'Accounting Report']);
      worksheet.addRow([`Period: ${startDate} to ${endDate}`]);
      worksheet.addRow([]);

      if (activeTab === 0) {
        // RCD Report headers
        worksheet.addRow(['Check Date', 'Check No.', 'DV No.', 'CAFOA No.', 'Payee Name', 'Particulars', 'Amount']);

        // Add data with missing check number rows inserted
        const rcdRows = buildRcdExportRows(rcdData);
        rcdRows.forEach((row) => {
          const excelRow = worksheet.addRow(row);
          // Format amount column (column 7) with 2 decimals and comma separator
          const amountCell = excelRow.getCell(7);
          if (row[6] !== null && row[6] !== '' && row[6] !== undefined) {
            amountCell.numFmt = '#,##0.00';
          }
        });
      } else {
        // Accounting Report headers
        worksheet.addRow(['Date', 'CAFOA No.', 'DV No.', 'Payee Name', 'Amount', 'Status']);

        // Add data
        accountingData.forEach(item => {
          const excelRow = worksheet.addRow([
            item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
            item.cafoa_number || '',
            item.dv_number || '',
            item.payee_name || item.voucher_payee || '',
            item.cafoa_amount ? parseFloat(item.cafoa_amount) : '',
            item.status || ''
          ]);
          // Format amount column (column 5) with 2 decimals and comma separator
          const amountCell = excelRow.getCell(5);
          if (item.cafoa_amount) {
            amountCell.numFmt = '#,##0.00';
          }
        });
      }

      // Style the header row
      const headerRow = worksheet.getRow(4);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6FA' }
      };

      // Auto-fit columns
      worksheet.columns.forEach(column => {
        column.width = 15;
      });

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${activeTab === 0 ? 'RCD_Report' : 'Accounting_Report'}_${startDate}_to_${endDate}.xlsx`);

    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export to Excel');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₱0.00';
    return `₱${parseFloat(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const buildRcdExportRows = (data) => {
    const rows = [];
    const sortedData = [...(data || [])].sort((a, b) => {
      const aNum = parseInt(a.check_number, 10);
      const bNum = parseInt(b.check_number, 10);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return aNum - bNum;
      }
      return (a.check_number || '').localeCompare(b.check_number || '', undefined, { numeric: true });
    });

    let previousNumber = null;
    sortedData.forEach((item) => {
      const currentNumber = item.check_number ? parseInt(item.check_number, 10) : NaN;
      if (
        previousNumber !== null &&
        !Number.isNaN(previousNumber) &&
        !Number.isNaN(currentNumber) &&
        currentNumber > previousNumber + 1
      ) {
        for (let missing = previousNumber + 1; missing < currentNumber; missing += 1) {
          rows.push(['', missing.toString(), '', '', '', '', null]);
        }
      }

      rows.push([
        item.check_date ? formatSafeLocalDate(item.check_date) : '',
        item.check_number || '',
        item.dv_number || '',
        item.cafoa_number || '',
        item.payee_name || item.voucher_payee || '',
        item.voucher_particulars || '',
        item.check_amount ? parseFloat(item.check_amount) : ''
      ]);

      if (!Number.isNaN(currentNumber)) {
        previousNumber = currentNumber;
      }
    });

    return rows;
  };

  const renderRCDTable = () => (
    <TableContainer component={Paper} ref={printRef}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Check Date</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Check No.</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>DV No.</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>CAFOA No.</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payee Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Particulars</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rcdData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No data available for the selected period</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rcdData.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  {item.check_date ? formatSafeLocalDate(item.check_date, 'en-PH') : ''}
                </TableCell>
                <TableCell>{item.check_number || ''}</TableCell>
                <TableCell>{item.dv_number || ''}</TableCell>
                <TableCell>{item.cafoa_number || ''}</TableCell>
                <TableCell>{item.payee_name || item.voucher_payee || ''}</TableCell>
                <TableCell>{item.voucher_particulars || ''}</TableCell>
                <TableCell align="right">{formatCurrency(item.check_amount)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAccountingTable = () => (
    <TableContainer component={Paper} ref={printRef}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>CAFOA No.</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>DV No.</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payee Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accountingData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No data available for the selected period</Typography>
              </TableCell>
            </TableRow>
          ) : (
            accountingData.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  {item.created_at ? new Date(item.created_at).toLocaleDateString('en-PH') : ''}
                </TableCell>
                <TableCell>{item.cafoa_number || ''}</TableCell>
                <TableCell>{item.dv_number || ''}</TableCell>
                <TableCell>{item.payee_name || item.voucher_payee || ''}</TableCell>
                <TableCell align="right">{formatCurrency(item.cafoa_amount)}</TableCell>
                <TableCell>
                  <Chip
                    label={item.status || 'pending'}
                    color={item.status === 'claimed' ? 'success' : item.status === 'check_issued' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon />
        E-Logbook Reports
      </Typography>

      {/* Date Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Box sx={{ minWidth: 200 }}>
          <Select
            value={voucherType}
            onChange={(e) => setVoucherType(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            {voucherTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type === 'all' ? 'All Voucher Types' : type}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab
            icon={<AccountBalanceIcon />}
            label="RCD"
            iconPosition="start"
          />
          <Tab
            icon={<AssessmentIcon />}
            label="Accounting"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading}
        >
          Print
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportExcel}
          disabled={loading}
        >
          Export to Excel
        </Button>
      </Stack>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Report Content */}
      {!loading && (
        <Box>
          {activeTab === 0 ? renderRCDTable() : renderAccountingTable()}
        </Box>
      )}
    </Container>
  );
};

export default LogBookReports;
